import React, { useState, useEffect, useCallback } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import { HiOutlineRefresh } from 'react-icons/hi';
import {
    FaImage, FaUpload, FaRegEye, FaLink, FaEdit, FaTrashAlt, FaTimes, FaListAlt,
    FaComment, FaThumbsUp, FaStar, FaEye, FaTimesCircle, FaSearch, FaAd, FaCopy,
    FaPlus
} from 'react-icons/fa';
import { IoIosSend } from "react-icons/io";
import NotFound from '../../components/UI/NotFound';
import FileUpload from '../../components/UI/FileUpload';
import generateCaptcha from '../../components/captcha';
import { imageType, invalidImageMessage } from '../../components/imageType';
import { formatView } from '../../components/formatView';
import { useNavigate } from 'react-router-dom';

// Product Notification Modal Component
const ProductNotificationModal = ({ isOpen, onClose, product }) => {
    const [notificationData, setNotificationData] = useState({
        title: product?.l_name || '',
        message: '',
        platform: 'both',
        productId: product?._id || ''
    });

    if (!isOpen || !product) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNotificationData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        // Validation
        if (!notificationData.title || !notificationData.message) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            const response = await Calling.productNotification(notificationData);

            if (response?.status) {
                toast.success(response?.message || 'Notification sent successfully!');
                onClose();
            } else {
                toast.error(response?.message || 'Failed to send notification');
            }
        } catch (error) {
            console.error('Error sending notification:', error);
            toast.error('An error occurred while sending the notification');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
                <div className="p-5 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-800">Send Product Notification</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                            Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={notificationData.title}
                            onChange={handleChange}
                            className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue"
                            placeholder="Notification title"
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Default: Product name
                        </p>
                    </div>

                    <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                            Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="message"
                            name="message"
                            value={notificationData.message}
                            onChange={handleChange}
                            rows="3"
                            className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue"
                            placeholder="Enter notification message"
                            required
                        ></textarea>
                    </div>

                    <div>
                        <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-1">
                            Platform
                        </label>
                        <select
                            id="platform"
                            name="platform"
                            value={notificationData.platform}
                            onChange={handleChange}
                            className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue"
                        >
                            <option value="both">Both (Android & iOS)</option>
                            <option value="android">Android Only</option>
                            <option value="web">Web Only</option>
                            <option value="ios">iOS Only</option>
                        </select>
                    </div>
                </div>

                <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                        <IoIosSend size={16} />
                        Send Notification
                    </button>
                </div>
            </div>
        </div>
    );
};
// View Modal Component
const ViewModal = ({ list, onClose }) => {
    if (!list) return null;

    // Format date to a readable format
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleCopyId = (id) => {
        navigator.clipboard.writeText(id);
        toast.success('ID copied to clipboard!');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="p-5 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-semibold text-gray-800">List Details</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <FaTimesCircle size={24} />
                    </button>
                </div>

                <div className="p-5 space-y-6">
                    {/* List Image and Basic Info */}
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Image */}
                        <div className="md:w-1/3 flex justify-center">
                            {list.l_image ? (
                                <div className="relative group">
                                    <img
                                        src={list.l_image}
                                        alt={list.l_name}
                                        className="h-48 w-48 object-cover rounded-lg shadow-md transition-transform duration-300 group-hover:scale-105"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://via.placeholder.com/200?text=No+Image';
                                        }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-end justify-center">
                                        <a
                                            href={list.l_image}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-white pb-3 text-sm font-medium"
                                        >
                                            View Full Image
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <div className="h-48 w-48 bg-gray-200 rounded-lg shadow-md flex items-center justify-center">
                                    <FaImage className="text-gray-400" size={48} />
                                </div>
                            )}
                        </div>

                        {/* Basic Info */}
                        <div className="md:w-2/3">
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 h-full">
                                <h3 className="text-lg font-semibold text-gray-800">{list.l_name || 'Untitled List'}</h3>

                                <div className="space-y-3">
                                    {/* Category */}
                                    <div className="flex items-start">
                                        <div className="w-28 text-sm font-medium text-gray-500">Category:</div>
                                        <div className="flex-1 text-sm font-medium text-gray-900">
                                            {list.category && list.category.category_name ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-main-blue/10 text-main-blue">
                                                    {list.category.category_name}
                                                </span>
                                            ) : 'N/A'}
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className="flex items-start">
                                        <div className="w-28 text-sm font-medium text-gray-500">Status:</div>
                                        <div>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${list.l_status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                {list.l_status ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Pro Feature */}
                                    <div className="flex items-start">
                                        <div className="w-28 text-sm font-medium text-gray-500">Pro Feature:</div>
                                        <div>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${list.l_pro ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                {list.l_pro ? 'Premium' : 'Free'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-start">
                                        <div className="w-28 text-sm font-medium text-gray-500">Ad Count:</div>
                                        <div className="flex-1 text-sm text-gray-900">
                                            {list.l_ads || 0}
                                        </div>
                                    </div>

                                    {/* Created At */}
                                    <div className="flex items-start">
                                        <div className="w-28 text-sm font-medium text-gray-500">Created:</div>
                                        <div className="flex-1 text-sm text-gray-900">
                                            {list.createdAt ? formatDate(list.createdAt) : 'N/A'}
                                        </div>
                                    </div>

                                    {/* Updated At */}
                                    <div className="flex items-start">
                                        <div className="w-28 text-sm font-medium text-gray-500">Updated:</div>
                                        <div className="flex-1 text-sm text-gray-900">
                                            {list.updatedAt ? formatDate(list.updatedAt) : 'N/A'}
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="w-28 text-sm font-medium text-gray-500">Views:</div>
                                        <div className="flex-1 text-sm text-gray-900">
                                            {formatView(list.l_view || 0)}
                                        </div>
                                    </div>

                                    {/* ID */}
                                    <div className="flex items-start gap-2">
                                        <div className="w-28 text-sm font-medium text-gray-500">List ID:</div>
                                        <div className="flex-1 text-sm text-gray-500 font-mono overflow-hidden text-ellipsis">
                                            {list._id}
                                        </div>
                                        <button
                                            onClick={() => handleCopyId(list._id)}
                                            className="flex items-center px-2 py-1 bg-gray-50 text-gray-600 rounded-md hover:bg-gray-100 transition-colors flex-shrink-0"
                                            title="Copy ID"
                                        >
                                            <FaCopy className="mr-1" />
                                            ID
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Prompt */}
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                        <div className="p-4 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-md font-semibold text-gray-800">Prompt</h3>
                        </div>
                        <div className="p-4">
                            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                    {list.l_prompt || 'No prompt available'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Recommended Model */}
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                        <div className="p-4 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-md font-semibold text-gray-800">Recommended Model</h3>
                        </div>
                        <div className="p-4">
                            {list.l_model_recommended ? (
                                <div className={`flex items-center space-x-4 p-3 rounded-lg border ${list.l_model_recommended.m_status ? 'bg-gray-50 border-gray-100' : 'bg-red-50 border-red-100'}`}>
                                    <div className="flex-shrink-0">
                                        {list.l_model_recommended.m_image ? (
                                            <img
                                                src={list.l_model_recommended.m_image}
                                                alt={list.l_model_recommended.m_name}
                                                className={`h-12 w-12 rounded-full object-cover border ${list.l_model_recommended.m_status ? 'border-gray-200' : 'border-red-200'}`}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://via.placeholder.com/48?text=AI';
                                                }}
                                            />
                                        ) : (
                                            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${list.l_model_recommended.m_status ? 'bg-blue-100' : 'bg-red-100'}`}>
                                                <FaImage className={list.l_model_recommended.m_status ? 'text-blue-400' : 'text-red-400'} />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="text-base font-medium text-gray-900">{list.l_model_recommended.m_name}</h4>
                                        <div className="flex items-center mt-1">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${list.l_model_recommended.m_status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {list.l_model_recommended.m_status ? 'Active' : 'Inactive'}
                                            </span>
                                            {!list.l_model_recommended.m_status && (
                                                <span className="ml-2 text-red-500 text-xs font-medium">
                                                    Warning: This model is currently disabled
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 italic">No recommended model set</p>
                            )}
                        </div>
                    </div>

                    {/* Compatible Models */}
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                        <div className="p-4 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-md font-semibold text-gray-800">Compatible Models</h3>
                        </div>
                        <div className="p-4">
                            {Array.isArray(list.l_model) && list.l_model.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {list.l_model.map((model, index) => (
                                        <div key={index} className={`flex items-center space-x-3 p-3 rounded-lg border ${model.m_status ? 'bg-gray-50 border-gray-100' : 'bg-red-50 border-red-100'}`}>
                                            <div className="flex-shrink-0">
                                                {model.m_image ? (
                                                    <img
                                                        src={model.m_image}
                                                        alt={model.m_name}
                                                        className={`h-10 w-10 rounded-full object-cover border ${model.m_status ? 'border-gray-200' : 'border-red-200'}`}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://via.placeholder.com/40?text=AI';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${model.m_status ? 'bg-blue-100' : 'bg-red-100'}`}>
                                                        <FaImage className={model.m_status ? 'text-blue-400' : 'text-red-400'} />
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-900">{model.m_name}</h4>
                                                <p className="text-xs text-gray-500">
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${model.m_status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                        {model.m_status ? 'Active' : 'Inactive'}
                                                    </span>
                                                    {!model.m_status && <span className="ml-1 text-red-500 text-xs">(Disabled)</span>}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 italic">No compatible models available</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-5 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const List = () => {
    const [loader, setLoader] = useState(false);
    const [lists, setLists] = useState([]);
    const [modelList, setModelList] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPreviousPage, setHasPreviousPage] = useState(false);
    const [nextPage, setNextPage] = useState(1);
    const [previousPage, setPreviousPage] = useState(1);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [fileProcessing, setFileProcessing] = useState({});
    const [fileStatus, setFileStatus] = useState({});
    const [error, setError] = useState(null);
    const [selectedLists, setSelectedLists] = useState([]);
    const [viewSort, setViewSort] = useState(null);
    const [proSort, setProSort] = useState(null);
    const [editList, setEditList] = useState(null);
    const navigate = useNavigate();
    const MAX_FILE_SIZE_IMAGE = 10 * 1024 * 1024 //10 MB
    const imageSizeExceededMessage = `The image file size should not exceed ${MAX_FILE_SIZE_IMAGE / (1024 * 1024)} MB.`;
    // State for view modal
    const [viewList, setViewList] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    // Form state
    const [listName, setListName] = useState('');
    const [listPrompt, setListPrompt] = useState('');
    const [selectedModels, setSelectedModels] = useState([]);
    const [recommendedModel, setRecommendedModel] = useState('');
    const [listStatus, setListStatus] = useState(true);
    const [listPro, setListPro] = useState(false);
    const [listAd, setListAd] = useState(0);

    // Add these new state variables for product notification
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [productToNotify, setProductToNotify] = useState(null);
    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = async () => {
        setLoader(true);
        try {
            // Fetch models
            const resp1 = await Calling.getAiModel({});
            if (resp1?.status) {
                setModelList(Array.isArray(resp1.data) ? resp1.data : []);
            } else {
                toast.error(resp1?.message || "Failed to fetch AI models");
            }

            // Fetch categories
            const resp2 = await Calling.getCategory({});

            if (resp2?.status) {
                setCategoryList(Array.isArray(resp2.data) ? resp2.data : []);
            } else {
                toast.error(resp2?.message || "Failed to fetch categories");
            }

            // Fetch lists
            const resp3 = await Calling.getList(page, limit, search, selectedCategory, viewSort, proSort);
            if (resp3?.status) {
                setLists(Array.isArray(resp3?.data) ? resp3.data : []);
                setTotalPages(resp3?.totalPages || 1);
                setHasNextPage(!!resp3?.hasNextPage);
                setHasPreviousPage(!!resp3?.hasPreviousPage);
                setNextPage(resp3?.nextPage || 1);
                setPreviousPage(resp3?.previousPage || 1);
                setTotalRecords(resp3?.total || 0);
            } else {
                toast.error(resp3?.message || "Failed to fetch lists");
            }
        } catch (error) {
            console.error("Fetch error:", error);
            toast.error("An error occurred while fetching data");
        } finally {
            setLoader(false);
        }
    };

    const handleCopyId = (id) => {
        navigator.clipboard.writeText(id);
        toast.success('ID copied to clipboard!');
    };

    useEffect(() => {
        fetchData();
    }, [page, limit, search, selectedCategory, viewSort, proSort]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const fieldName = event.target.name || 'image';

        setError(null);
        setSelectedFile(null);
        setFileProcessing({ ...fileProcessing, [fieldName]: true });
        setFileStatus({ ...fileStatus, [fieldName]: null });
        setPreviewImage(null);

        if (!file) {
            setError('No file selected.');
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (!imageType.includes(file.type)) {
            setError(invalidImageMessage);
            toast.error(invalidImageMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (file.size > MAX_FILE_SIZE_IMAGE) {
            setError(imageSizeExceededMessage);
            toast.error(imageSizeExceededMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        setSelectedFile(file);
        setFileProcessing({ ...fileProcessing, [fieldName]: false });
        setFileStatus({ ...fileStatus, [fieldName]: 'success' });

        // Create preview URL for the selected file
        const previewUrl = URL.createObjectURL(file);
        setPreviewImage(previewUrl);
        setShowPreview(true);
    };

    const resetForm = () => {
        setSelectedFile(null);
        setListName('');
        setListPrompt('');
        setSelectedModels([]);
        setRecommendedModel('');
        setListPro(false);
        setListAd(0);
        setError(null);
        setFileStatus({});
        setEditList(null);
        setPreviewImage(null);
        setShowPreview(false);
        setListStatus(true);
    };

    const handleSubmit = async (e, listId = null) => {
        e.preventDefault();

        // Validation for new list creation
        if (!listId && (!selectedFile || !listPrompt || !selectedCategory || selectedModels.length === 0 || !recommendedModel)) {
            setError('Please fill all required fields except list name for new lists.');
            return;
        }

        // For edit mode, at least one field should be provided and list name is required
        if (listId && !listName) {
            setError('List name is required when updating a list.');
            return;
        }

        if (listId && !selectedFile && !listPrompt && !selectedCategory && selectedModels.length === 0 && !recommendedModel) {
            setError('Please update at least one field.');
            return;
        }

        setLoader(true);
        try {
            const formData = new FormData();
            if (selectedFile) {
                formData.append('image', selectedFile);
            }

            // Always include list name in updates
            if (listId) {
                formData.append('l_name', listName);
            }

            if (listPrompt) {
                formData.append('l_prompt', listPrompt);
            }
            if (selectedCategory) {
                formData.append('category', selectedCategory);
            }
            if (selectedModels.length > 0) {
                selectedModels.forEach(model => {
                    formData.append('l_model[]', model);
                });
            }
            if (recommendedModel) {
                formData.append('l_model_recommended', recommendedModel);
            }
            formData.append('l_pro', listPro);
            formData.append('l_ads', listAd);

            // Include status for edit mode
            if (listId) {
                formData.append('l_status', listStatus);
            }

            let resp;
            if (listId) {
                resp = await Calling.updateList(listId, formData);
            } else {
                resp = await Calling.addList(formData);
            }

            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
                fetchData();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Submission error:", error.response?.data || error.message);
            toast.error("Something went wrong.");
        } finally {
            setLoader(false);
        }
    };

    const handleOpenNotificationModal = (product) => {
        setProductToNotify(product);
        setShowNotificationModal(true);
    };

    // Function to close the notification modal
    const closeNotificationModal = () => {
        setShowNotificationModal(false);
        setProductToNotify(null);
    };

    const handleEdit = (list) => {
        setEditList(list._id);
        setListName(list.l_name || '');
        setListPrompt(list.l_prompt || '');
        // Check if category exists and has an _id property
        setSelectedCategory(list.category && list.category._id ? list.category._id : '');
        // Safely map l_model array, ensuring it exists and each model has an _id
        setSelectedModels(Array.isArray(list.l_model)
            ? list.l_model.filter(model => model && model._id).map(model => model._id)
            : []);
        // Check if l_model_recommended exists and has an _id property
        setRecommendedModel(list.l_model_recommended && list.l_model_recommended._id
            ? list.l_model_recommended._id
            : '');

        setListPro(!!list.l_pro);
        setListAd(list.l_ads || 0);
        setListStatus(!!list.l_status);
        setPreviewImage(list.l_image || null);
        setShowPreview(!!list.l_image);
        setFileStatus({});

        // Scroll to the form with smooth animation
        setTimeout(() => {
            const formElement = document.getElementById('listForm');
            if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                // Fallback if the form element isn't found
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        }, 100); // Small delay to ensure state updates have completed
    };

    const handleDelete = async () => {
        if (selectedLists.length === 0) {
            toast.error('Please select at least one list to delete.');
            return;
        }

        const content = "You are about to delete selected items. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            const resp = await Calling.deleteList({ id: selectedLists });
            if (resp?.status) {
                toast.success(resp?.message);
                setLists(lists.filter((list) => !selectedLists.includes(list._id)));
                setSelectedLists([]);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Delete error:", error.response?.data || error.message);
            toast.error("Failed to delete lists.");
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateStatus = async (id, type) => {

        setLoader(true);
        try {
            // Get the current list
            const currentList = lists.find(list => list._id === id);
            if (!currentList) {
                toast.error("List not found");
                setLoader(false);
                return;
            }

            // Create formData with updated status (toggled)
            const formData = new FormData();
            formData.append('l_status', type === 'status' ? !currentList.l_status : currentList.l_status);

            // Always include the list name for updates
            formData.append('l_name', currentList.l_name || '');

            // Include other fields
            formData.append('l_prompt', currentList.l_prompt || '');
            formData.append('l_pro', type === 'pro' ? !currentList.l_pro : currentList.l_pro);
            formData.append('l_ads', currentList.l_ads || 0);

            // Safely append category
            if (currentList.category && currentList.category._id) {
                formData.append('category', currentList.category._id);
            }

            // Safely append models
            if (Array.isArray(currentList.l_model)) {
                currentList.l_model.forEach(model => {
                    if (model && model._id) {
                        formData.append('l_model[]', model._id);
                    }
                });
            }

            // Safely append recommended model
            if (currentList.l_model_recommended && currentList.l_model_recommended._id) {
                formData.append('l_model_recommended', currentList.l_model_recommended._id);
            }

            const resp = await Calling.updateList(id, formData);
            if (resp?.status) {
                toast.success(resp?.message);
                fetchData();
            } else {
                toast.error(resp?.message || "Failed to update status");
            }
        } catch (error) {
            console.error("Status update error:", error.response?.data || error.message);
            toast.error("Failed to update status.");
        } finally {
            setLoader(false);
        }
    };

    const handleSelectList = (id) => {
        if (selectedLists.includes(id)) {
            setSelectedLists(selectedLists.filter(listId => listId !== id));
        } else {
            setSelectedLists([...selectedLists, id]);
        }
    };

    const handleAddStory = async () => {
        if (selectedLists.length === 0) {
            toast.error('Please select at least one list to add.');
            return;
        }

        const content = "You are about to add stories to selected lists. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            // Send l_id array to match backend
            const resp = await Calling.addStory({ l_id: selectedLists });

            if (resp?.status) {
                toast.success(resp?.message);
                setSelectedLists([]);
            } else {
                toast.error(resp?.message || "Failed to add stories.");
            }
        } catch (error) {
            console.error("Add story error:", error.response?.data || error.message);
            toast.error("Failed to add stories.");
        } finally {
            setLoader(false);
        }
    };


    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedLists(lists.map(list => list._id));
        } else {
            setSelectedLists([]);
        }
    };

    const closePreview = () => {
        setShowPreview(false);
    };

    const handleModelChange = (e, modelId) => {
        if (e.target.checked) {
            setSelectedModels([...selectedModels, modelId]);
        } else {
            setSelectedModels(selectedModels.filter(id => id !== modelId));
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handleView = (list) => {
        setViewList(list);
        setShowViewModal(true);
    };

    const closeViewModal = () => {
        setShowViewModal(false);
        setViewList(null);
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        setPage(1); // Reset to first page when changing category
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        debouncedSearch('');
    };

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((value) => {
            setSearch(value);
            setPage(1); // Reset to first page when searching
        }, 500),
        []
    );

    // Debounce helper function
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    return (
        <>
            <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                <div className="mx-auto">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">List Management</h1>
                            <p className="mt-1 text-sm text-gray-500">Manage lists available in your application</p>
                        </div>
                        <button
                            onClick={fetchData}
                            className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/20 rounded-lg text-white transition-colors duration-200 shadow-sm"
                        >
                            <HiOutlineRefresh className="text-lg" />
                            <span>Refresh</span>
                        </button>
                    </div>

                    {/* Add List Card */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100 mb-6">
                        <div className="p-5 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                    <FaUpload className="text-main-blue text-xl" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-800">{editList ? 'Update List' : 'Add List'}</h2>
                                    <p className="text-xs text-gray-500 mt-0.5">{editList ? 'Update existing list' : 'Add new list to your application'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-5 bg-gray-50/50">
                            <form
                                id="listForm"
                                onSubmit={(e) => handleSubmit(e, editList)}
                                className="space-y-4 add-list-form"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaImage className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                List Image
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Upload list icon or thumbnail</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="relative">
                                            <FileUpload
                                                name="image"
                                                id="image"
                                                onChange={handleFileChange}
                                                accept={imageType.join(',')}
                                                required={!editList}
                                                multiple={false}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:bg-main-blue transition duration-200"
                                            />
                                            {fileProcessing['image'] && (
                                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                    <div className="loader"></div>
                                                </div>
                                            )}
                                            {fileStatus['image'] === 'error' && (
                                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-red-500">✘</div>
                                            )}
                                            {fileStatus['image'] === 'success' && (
                                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-green-500">✓</div>
                                            )}
                                        </div>
                                        <p className="text-xs mt-1 text-gray-400">Allow png, jpg, jpeg / Max size: {MAX_FILE_SIZE_IMAGE / (1024 * 1024)} MB</p>

                                        {/* Image Preview */}
                                        {showPreview && previewImage && (
                                            <div className="mt-3 relative">
                                                <div className="border rounded-lg p-2 inline-block relative">
                                                    <button
                                                        type="button"
                                                        onClick={closePreview}
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                    <img
                                                        src={previewImage}
                                                        alt="Preview"
                                                        className="h-24 w-24 object-contain rounded"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* List Name Field - Only visible during edit/update */}
                                {editList && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaEdit className="text-main-blue text-sm" />
                                                <label className="block text-sm font-medium text-gray-700">
                                                    List Name
                                                    <span className="text-red-500 ml-1">*</span>
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">
                                                Enter list name (required for updates)
                                            </p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <input
                                                type="text"
                                                name="listName"
                                                id="listName"
                                                value={listName}
                                                onChange={(e) => setListName(e.target.value)}
                                                required={!!editList}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                                                placeholder="e.g., Portrait Photography, Digital Art"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaComment className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Prompt
                                                {!editList && <span className="text-red-500 ml-1">*</span>}
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Enter default prompt</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <textarea
                                            name="listPrompt"
                                            id="listPrompt"
                                            value={listPrompt}
                                            onChange={(e) => setListPrompt(e.target.value)}
                                            required={!editList}
                                            rows={4}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                                            placeholder="Enter default prompt for this list..."
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaListAlt className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Category
                                                {!editList && <span className="text-red-500 ml-1">*</span>}
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Select category for this list</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <select
                                            name="category"
                                            id="category"
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value)}
                                            required={!editList}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                        >
                                            <option value="">Select Category</option>
                                            {categoryList.map((category) => (
                                                <option key={category._id} value={category._id}>
                                                    {category.category_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaLink className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Compatible Models
                                                {!editList && <span className="text-red-500 ml-1">*</span>}
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Select AI models compatible with this list</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                                            {modelList.length > 0 ? (
                                                modelList.map((model) => (
                                                    <div key={model._id} className="flex items-center mb-2 last:mb-0">
                                                        <input
                                                            type="checkbox"
                                                            id={`model-${model._id}`}
                                                            checked={selectedModels.includes(model._id)}
                                                            onChange={(e) => handleModelChange(e, model._id)}
                                                            disabled={!model.m_status}
                                                            className={`h-4 w-4 focus:ring-main-blue border-gray-300 rounded ${model.m_status
                                                                ? 'text-main-blue cursor-pointer'
                                                                : 'text-gray-300 cursor-not-allowed bg-gray-100'
                                                                }`}
                                                        />
                                                        <label
                                                            htmlFor={`model-${model._id}`}
                                                            className={`ml-2 block text-sm ${model.m_status
                                                                ? 'text-gray-700'
                                                                : 'text-gray-400'
                                                                } ${!model.m_status ? 'line-through' : ''}`}
                                                        >
                                                            {model.m_name}
                                                            {!model.m_status && <span className="ml-2 text-xs text-red-500">(Inactive)</span>}
                                                        </label>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="text-sm text-gray-500">No models available. Please add models first.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaStar className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Recommended Model
                                                {!editList && <span className="text-red-500 ml-1">*</span>}
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Select the recommended AI model for this list</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <select
                                            name="recommendedModel"
                                            id="recommendedModel"
                                            value={recommendedModel}
                                            onChange={(e) => setRecommendedModel(e.target.value)}
                                            required={!editList}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                        >
                                            <option value="">Select Recommended Model</option>
                                            {modelList
                                                .filter(model => selectedModels.includes(model._id))
                                                .map((model) => (
                                                    <option
                                                        key={model._id}
                                                        value={model._id}
                                                        disabled={!model.m_status}
                                                        className={!model.m_status ? 'text-gray-400' : ''}
                                                    >
                                                        {model.m_name} {!model.m_status ? '(Inactive)' : ''}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaThumbsUp className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Pro Feature
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Is this a premium feature?</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={listPro}
                                                onChange={() => setListPro(!listPro)}
                                                className="sr-only peer"
                                            />
                                            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                                    peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                    after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                    after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                    after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                                    peer-checked:bg-main-blue"></div>
                                            <span className="ml-3 text-sm font-medium text-gray-700">
                                                {listPro ? 'Pro Feature' : 'Free Feature'}
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaAd className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Ad Count
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Number of ads to show (0 for no ads)</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <input
                                            type="number"
                                            name="listAd"
                                            id="listAd"
                                            value={listAd}
                                            onChange={(e) => setListAd(Math.max(0, parseInt(e.target.value) || 0))}
                                            min="0"
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                            placeholder="0"
                                        />
                                        <p className={`text-xs mt-2 text-gray-500`}>Minimum value: 0</p>
                                        <p className={`text-xs mt-2 ${error ? 'text-red-500' : 'text-gray-500'}`}>{error ? error : ''}</p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        Reset
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors duration-200 flex items-center gap-2"
                                    >
                                        <FaUpload className="text-sm" />
                                        {editList ? 'Update List' : 'Save List'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Search and Filter */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 mb-6">
                        <div className="p-5">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-2">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FaSearch className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Search by name..."
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                        />
                                        {searchTerm && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                <button
                                                    onClick={handleClearSearch}
                                                    className="text-gray-400 hover:text-gray-600"
                                                >
                                                    <FaTimes className="h-5 w-5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <select
                                        value={selectedCategory}
                                        onChange={handleCategoryChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                    >
                                        <option value="">All Categories</option>
                                        {categoryList.map((category) => (
                                            <option key={category._id} value={category._id}>
                                                {category.category_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lists Table Card */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
                        <div className="p-5 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                        <FaRegEye className="text-main-blue text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Lists</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            Manage your existing lists
                                            <span className="ml-1 px-2 py-0.5 bg-main-blue/10 text-main-blue rounded-full text-xs font-medium">
                                                Total: {totalRecords}
                                            </span>
                                            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition
                                                ${viewSort
                                                    ? "bg-main-blue text-white" // Active design
                                                    : "bg-main-blue/10 text-main-blue" // Inactive design
                                                }`} onClick={() => setViewSort(!viewSort)}>
                                                Top view
                                            </span>
                                            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition
                                                ${proSort
                                                    ? "bg-main-blue text-white" // Active design
                                                    : "bg-main-blue/10 text-main-blue" // Inactive design
                                                }`} onClick={() => setProSort(!proSort)}>
                                                Pro
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                {selectedLists.length > 0 && (
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={handleDelete}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-2"
                                        >
                                            <FaTrashAlt className="text-sm" />
                                            Delete Selected ({selectedLists.length})
                                        </button>
                                        <button
                                            onClick={handleAddStory}
                                            disabled={loader || selectedLists.length === 0}
                                            className={`px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2 
                                                        ${loader || selectedLists.length === 0
                                                    ? "bg-green-400 cursor-not-allowed"
                                                    : "bg-green-600 hover:bg-green-700 text-white"}`}
                                        >
                                            <FaPlus className="text-sm" />
                                            Add To Story ({selectedLists.length})
                                        </button>
                                    </div>

                                )}
                            </div>
                        </div>

                        <div className="p-5 bg-gray-50/50">
                            {loader ? (
                                <div className="flex justify-center items-center py-12">
                                    <Loader loader={loader} />
                                </div>
                            ) : lists.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-main-th">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 text-main-blue focus:ring-main-blue border-gray-300 rounded"
                                                        onChange={handleSelectAll}
                                                        checked={selectedLists.length === lists.length && lists.length > 0}
                                                    />
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Image
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Name
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Category
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Views
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Pro
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {lists.map((list) => (
                                                <tr key={list._id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 text-main-blue focus:ring-main-blue border-gray-300 rounded"
                                                            onChange={() => handleSelectList(list._id)}
                                                            checked={selectedLists.includes(list._id)}
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex-shrink-0 h-10 w-10">
                                                            {list.l_image ? (
                                                                <img
                                                                    className="h-10 w-10 rounded-full object-cover"
                                                                    src={list.l_image}
                                                                    alt={list.l_name || 'List image'}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = 'https://via.placeholder.com/40?text=Error';
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                                    <FaImage className="text-gray-400" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">{list.l_name || 'Untitled'}</div>
                                                        <div className="text-xs text-gray-500 max-w-xs truncate">
                                                            {list.l_prompt ? `${list.l_prompt.substring(0, 50)}...` : 'No prompt available'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            {list.category && list.category.category_name ? list.category.category_name : 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            <button onClick={() => navigate(`/views/dashboard/${list._id}`)} className="text-blue-600 hover:text-blue-900 mr-3">
                                                                {formatView(list.l_view || 0)}
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!list.l_pro}
                                                                onChange={() => handleUpdateStatus(list._id, 'pro')}
                                                                className="sr-only peer"
                                                            />
                                                            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                                                        peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                                        after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                                        after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                                        after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                                                        peer-checked:bg-main-blue"></div>
                                                        </label>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!list.l_status}
                                                                onChange={() => handleUpdateStatus(list._id, 'status')}
                                                                className="sr-only peer"
                                                            />
                                                            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                                                        peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                                        after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                                        after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                                        after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                                                        peer-checked:bg-green-500"></div>
                                                        </label>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleView(list)}
                                                                className="flex items-center px-2 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
                                                                title="View Details"
                                                            >
                                                                <FaEye />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(list)}
                                                                className="flex items-center px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                                                                title="Edit List"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                onClick={() => handleOpenNotificationModal(list)}
                                                                className="flex items-center px-2 py-1 bg-green-50 text-green-600 rounded-md hover:bg-green-100 transition-colors"
                                                                title="Send Notification"
                                                            >
                                                                <IoIosSend />
                                                            </button>
                                                            <button
                                                                onClick={() => handleCopyId(list._id)}
                                                                className="flex items-center px-2 py-1 bg-yellow-50 text-yellow-600 rounded-md hover:bg-yellow-100 transition-colors flex-shrink-0"
                                                                title="Copy ID"
                                                            >
                                                                <FaCopy />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <NotFound />
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
                                    <div className="flex flex-1 justify-between sm:hidden">
                                        <button
                                            onClick={() => handlePageChange(page - 1)}
                                            disabled={!hasPreviousPage}
                                            className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 ${!hasPreviousPage ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(page + 1)}
                                            disabled={!hasNextPage}
                                            className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 ${!hasNextPage ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                        >
                                            Next
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700">
                                                Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
                                                <span className="font-medium">{Math.min(page * limit, lists.length)}</span> of{' '}
                                                <span className="font-medium">{lists.length}</span> results
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                                <button
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={page === 1}
                                                    className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ${page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                                >
                                                    <span className="sr-only">First</span>
                                                    <span className="text-xs">First</span>
                                                </button>
                                                <button
                                                    onClick={() => handlePageChange(page - 1)}
                                                    disabled={!hasPreviousPage}
                                                    className={`relative inline-flex items-center px-2 py-2 text-gray-400 ${!hasPreviousPage ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                                >
                                                    <span className="sr-only">Previous</span>
                                                    <span className="text-xs">Prev</span>
                                                </button>
                                                {/* Dynamic Page Numbers */}
                                                {(() => {
                                                    const pageNumbers = [];
                                                    let startPage = Math.max(1, page - 2);
                                                    let endPage = Math.min(totalPages, startPage + 4);

                                                    // Adjust start page if we're near the end
                                                    if (endPage - startPage < 4 && startPage > 1) {
                                                        startPage = Math.max(1, endPage - 4);
                                                    }

                                                    // First page button if not in range
                                                    if (startPage > 1) {
                                                        pageNumbers.push(
                                                            <button
                                                                key="page-1"
                                                                onClick={() => handlePageChange(1)}
                                                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50`}
                                                            >
                                                                1
                                                            </button>
                                                        );

                                                        // Ellipsis if there's a gap
                                                        if (startPage > 2) {
                                                            pageNumbers.push(
                                                                <span key="ellipsis-1" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700">
                                                                    ...
                                                                </span>
                                                            );
                                                        }
                                                    }

                                                    // Page numbers
                                                    for (let i = startPage; i <= endPage; i++) {
                                                        pageNumbers.push(
                                                            <button
                                                                key={`page-${i}`}
                                                                onClick={() => handlePageChange(i)}
                                                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${page === i
                                                                    ? 'bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                                                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                                                    }`}
                                                            >
                                                                {i}
                                                            </button>
                                                        );
                                                    }

                                                    // Last page button if not in range
                                                    if (endPage < totalPages) {
                                                        // Ellipsis if there's a gap
                                                        if (endPage < totalPages - 1) {
                                                            pageNumbers.push(
                                                                <span key="ellipsis-2" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700">
                                                                    ...
                                                                </span>
                                                            );
                                                        }

                                                        pageNumbers.push(
                                                            <button
                                                                key={`page-${totalPages}`}
                                                                onClick={() => handlePageChange(totalPages)}
                                                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50`}
                                                            >
                                                                {totalPages}
                                                            </button>
                                                        );
                                                    }

                                                    return pageNumbers;
                                                })()}
                                                <button
                                                    onClick={() => handlePageChange(page + 1)}
                                                    disabled={!hasNextPage}
                                                    className={`relative inline-flex items-center px-2 py-2 text-gray-400 ${!hasNextPage ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                                >
                                                    <span className="sr-only">Next</span>
                                                    <span className="text-xs">Next</span>
                                                </button>
                                                <button
                                                    onClick={() => handlePageChange(totalPages)}
                                                    disabled={page === totalPages}
                                                    className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ${page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                                >
                                                    <span className="sr-only">Last</span>
                                                    <span className="text-xs">Last</span>
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* View Modal */}
            {showViewModal && <ViewModal list={viewList} onClose={closeViewModal} />}

            {/* Product Notification Modal */}
            {showNotificationModal && (
                <ProductNotificationModal
                    isOpen={showNotificationModal}
                    onClose={closeNotificationModal}
                    product={productToNotify}
                />
            )}
        </>
    );
};

export default List;